import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InstrumentPerformanceTable = ({ dateRange, selectedAccounts }) => {
  const [sortBy, setSortBy] = useState('profit');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock instrument performance data
  const instrumentData = [
    {
      symbol: 'EURUSD',
      name: 'Euro / US Dollar',
      trades: 145,
      winRate: 72.4,
      profit: 12847.32,
      profitPercentage: 8.2,
      avgTrade: 88.60,
      maxDrawdown: -2.1,
      volume: 2450000,
      commission: -245.50,
      swap: -89.20,
      category: 'Major'
    },
    {
      symbol: 'GBPUSD',
      name: 'British Pound / US Dollar',
      trades: 98,
      winRate: 68.4,
      profit: 8934.21,
      profitPercentage: 5.7,
      avgTrade: 91.16,
      maxDrawdown: -1.8,
      volume: 1680000,
      commission: -168.00,
      swap: -45.80,
      category: 'Major'
    },
    {
      symbol: 'USDJPY',
      name: 'US Dollar / Japanese Yen',
      trades: 87,
      winRate: 65.5,
      profit: 6721.45,
      profitPercentage: 4.3,
      avgTrade: 77.25,
      maxDrawdown: -2.5,
      volume: 1450000,
      commission: -145.00,
      swap: 23.40,
      category: 'Major'
    },
    {
      symbol: 'AUDUSD',
      name: 'Australian Dollar / US Dollar',
      trades: 76,
      winRate: 71.1,
      profit: 5432.18,
      profitPercentage: 3.5,
      avgTrade: 71.48,
      maxDrawdown: -1.6,
      volume: 1120000,
      commission: -112.00,
      swap: -67.30,
      category: 'Major'
    },
    {
      symbol: 'USDCAD',
      name: 'US Dollar / Canadian Dollar',
      trades: 65,
      winRate: 63.1,
      profit: 4156.89,
      profitPercentage: 2.7,
      avgTrade: 63.95,
      maxDrawdown: -1.9,
      volume: 980000,
      commission: -98.00,
      swap: -12.50,
      category: 'Major'
    },
    {
      symbol: 'EURGBP',
      name: 'Euro / British Pound',
      trades: 54,
      winRate: 59.3,
      profit: 2847.63,
      profitPercentage: 1.8,
      avgTrade: 52.73,
      maxDrawdown: -2.3,
      volume: 750000,
      commission: -75.00,
      swap: -34.20,
      category: 'Cross'
    },
    {
      symbol: 'EURJPY',
      name: 'Euro / Japanese Yen',
      trades: 43,
      winRate: 67.4,
      profit: 1923.45,
      profitPercentage: 1.2,
      avgTrade: 44.73,
      maxDrawdown: -1.4,
      volume: 620000,
      commission: -62.00,
      swap: 15.80,
      category: 'Cross'
    },
    {
      symbol: 'GOLD',
      name: 'Gold Spot',
      trades: 32,
      winRate: 75.0,
      profit: 3421.87,
      profitPercentage: 2.2,
      avgTrade: 106.93,
      maxDrawdown: -1.1,
      volume: 450000,
      commission: -90.00,
      swap: -23.40,
      category: 'Commodity'
    }
  ];

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortedData = [...instrumentData]?.sort((a, b) => {
    const aValue = a?.[sortBy];
    const bValue = b?.[sortBy];
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(value);
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(1)}%`;
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US')?.format(value);
  };

  const getProfitColor = (value) => {
    return value >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const renderMiniBarChart = (value, max) => {
    const percentage = Math.abs(value) / max * 100;
    const color = value >= 0 ? 'bg-green-500' : 'bg-red-500';
    
    return (
      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  const maxProfit = Math.max(...instrumentData?.map(item => Math.abs(item?.profit)));

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return (
      <Icon 
        name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Instrument Performance
          </h3>
          <p className="text-sm text-muted-foreground">
            Trading performance breakdown by instrument
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
        >
          Export
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('symbol')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Instrument</span>
                  <SortIcon column="symbol" />
                </button>
              </th>
              <th className="text-right py-3 px-2">
                <button
                  onClick={() => handleSort('trades')}
                  className="flex items-center justify-end space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Trades</span>
                  <SortIcon column="trades" />
                </button>
              </th>
              <th className="text-right py-3 px-2">
                <button
                  onClick={() => handleSort('winRate')}
                  className="flex items-center justify-end space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Win Rate</span>
                  <SortIcon column="winRate" />
                </button>
              </th>
              <th className="text-right py-3 px-2">
                <button
                  onClick={() => handleSort('profit')}
                  className="flex items-center justify-end space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>P&L</span>
                  <SortIcon column="profit" />
                </button>
              </th>
              <th className="text-right py-3 px-2">
                <button
                  onClick={() => handleSort('avgTrade')}
                  className="flex items-center justify-end space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Avg Trade</span>
                  <SortIcon column="avgTrade" />
                </button>
              </th>
              <th className="text-right py-3 px-2">
                <button
                  onClick={() => handleSort('maxDrawdown')}
                  className="flex items-center justify-end space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Max DD</span>
                  <SortIcon column="maxDrawdown" />
                </button>
              </th>
              <th className="text-center py-3 px-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Performance
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((instrument, index) => (
              <tr 
                key={instrument?.symbol}
                className="border-b border-border hover:bg-muted/50 transition-colors duration-200"
              >
                <td className="py-4 px-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">
                        {instrument?.symbol?.slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-foreground text-sm">
                        {instrument?.symbol}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {instrument?.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-2 text-right">
                  <span className="text-sm font-medium text-foreground">
                    {formatNumber(instrument?.trades)}
                  </span>
                </td>
                <td className="py-4 px-2 text-right">
                  <span className="text-sm font-medium text-foreground">
                    {formatPercentage(instrument?.winRate)}
                  </span>
                </td>
                <td className="py-4 px-2 text-right">
                  <div className="space-y-1">
                    <div className={`text-sm font-medium ${getProfitColor(instrument?.profit)}`}>
                      {formatCurrency(instrument?.profit)}
                    </div>
                    <div className={`text-xs ${getProfitColor(instrument?.profitPercentage)}`}>
                      {formatPercentage(instrument?.profitPercentage)}
                    </div>
                  </div>
                </td>
                <td className="py-4 px-2 text-right">
                  <span className={`text-sm font-medium ${getProfitColor(instrument?.avgTrade)}`}>
                    {formatCurrency(instrument?.avgTrade)}
                  </span>
                </td>
                <td className="py-4 px-2 text-right">
                  <span className="text-sm font-medium text-red-600">
                    {formatPercentage(instrument?.maxDrawdown)}
                  </span>
                </td>
                <td className="py-4 px-2 text-center">
                  <div className="flex items-center justify-center">
                    {renderMiniBarChart(instrument?.profit, maxProfit)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-sm text-muted-foreground">Total Instruments</div>
            <div className="text-lg font-semibold text-foreground">
              {instrumentData?.length}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Profitable</div>
            <div className="text-lg font-semibold text-green-600">
              {instrumentData?.filter(item => item?.profit > 0)?.length}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Total Trades</div>
            <div className="text-lg font-semibold text-foreground">
              {formatNumber(instrumentData?.reduce((sum, item) => sum + item?.trades, 0))}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Total Volume</div>
            <div className="text-lg font-semibold text-foreground">
              {formatNumber(instrumentData?.reduce((sum, item) => sum + item?.volume, 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstrumentPerformanceTable;